"use client";

import { groupDrillsForPractice, methodLabel } from "@tamariba/drills";
import { useRef, useState } from "react";
import { useAuthoringScope } from "../authoring-scope";
import { LayoutPreview, LayoutPreviewProvider } from "../layout-preview";
import { PracticeToolIcon } from "../practice-tool-icon";
import { DrillBreadcrumbs } from "./breadcrumbs";
import { DrillCollectionOrderEditor } from "./collection-order";
import { DrillIcon } from "./icon";
import { MasteryProgress } from "./mastery";
import { collectionHasCoOwners, collectionOwnerLabel } from "./owners";
import { DrillPerformance } from "./performance";
import type { PracticePlanChoice } from "./practice-plan";
import { DrillShell } from "./shared";
import { type DrillCollectionDetail, dateLabel } from "./types";

export function DrillLibrary({
  collection,
  onEdit,
  onOrdered,
  planChoice,
  onEditPlan,
}: {
  collection: DrillCollectionDetail;
  onEdit: () => void;
  onOrdered: (collection: DrillCollectionDetail) => void;
  planChoice?: PracticePlanChoice;
  onEditPlan: () => void;
}) {
  const authoring = useAuthoringScope();
  const plan = planChoice?.selected ?? planChoice?.recommended;
  const drills = plan
    ? collection.drills.map((drill) => ({
        ...drill,
        collection: {
          ...drill.collection,
          practiceMethod: plan.practiceMethod,
          masteryPlan: plan.masteryPlan,
        },
      }))
    : collection.drills;
  const createHref = authoring.href(`/drills/new?collection=${collection.id}`);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<"all" | "learning" | "mastered">("all");
  const [reordering, setReordering] = useState(false);
  const orderButton = useRef<HTMLButtonElement>(null);
  function closeOrder() {
    setReordering(false);
    requestAnimationFrame(() => orderButton.current?.focus());
  }
  const masteredCount = authoring.admin
    ? 0
    : drills.filter((drill) => drill.mastery.mastered).length;
  const shown = drills?.filter(
    (drill) =>
      (authoring.admin ||
        filter === "all" ||
        (filter === "mastered"
          ? drill.mastery.mastered
          : !drill.mastery.mastered)) &&
      `${drill.title} ${drill.goal} ${drill.layout.title}`
        .toLocaleLowerCase("ja")
        .includes(query.toLocaleLowerCase("ja")),
  );
  const groups = authoring.admin
    ? [{ id: "all" as const, drills: shown }]
    : groupDrillsForPractice(
        shown,
        new Date(`${collection.practiceDay}T00:00:00+09:00`),
      );
  const groupTitles = {
    all: "ドリル一覧",
    ready: "今取り組む",
    waiting: "次回の練習待ち",
    mastered: "マスター",
  };
  if (reordering && collection.canEdit && !collection.isPlaylist)
    return (
      <DrillShell>
        <DrillBreadcrumbs collection={collection} />
        <DrillCollectionOrderEditor
          collection={collection}
          onSaved={(updated) => {
            onOrdered(updated);
            closeOrder();
          }}
          onCancel={closeOrder}
        />
      </DrillShell>
    );
  return (
    <DrillShell>
      <div className="drill-library-design drill-collection-view">
        <DrillBreadcrumbs collection={collection} />
        <div className="library-design-heading">
          <div>
            <h1>{collection.title}</h1>
            <p className="library-design-description">
              {collection.description ||
                (collection.isPlaylist
                  ? "選択したドリルを、元の練習設定で取り組めます。"
                  : collection.canEdit
                    ? "このドリル集に、練習する配置を追加しましょう。"
                    : "ドリルを選んで練習を始めましょう。")}
            </p>
          </div>
          {collection.canEdit && (
            <div className="library-design-heading-actions">
              {!authoring.admin && !collection.isPlaylist && (
                <a
                  className="secondary-button"
                  href={`/drills/collections/${collection.id}/sale`}
                >
                  販売設定
                </a>
              )}
              {authoring.admin && (
                <a
                  className="secondary-button"
                  href={`/admin/drill-collections/${collection.id}`}
                >
                  公開先を管理
                </a>
              )}
              <button
                type="button"
                className="library-design-settings-button"
                onClick={onEdit}
              >
                <DrillIcon name="edit" size={16} /> 設定を編集
              </button>
              {drills.length > 0 && !collection.isPlaylist && (
                <a className="primary-button" href={createHref}>
                  <DrillIcon name="plus" size={18} /> ドリルを追加
                </a>
              )}
            </div>
          )}
        </div>
        {(!collection.canEdit || collectionHasCoOwners(collection)) && (
          <p className="drill-provider">
            提供者: {collectionOwnerLabel(collection)} ·
            練習の進捗と記録はプレイヤーごとに保存されます。
          </p>
        )}
        {!collection.isPlaylist && (
          <section
            className="library-design-method-strip"
            aria-label="すべてのドリルに共通の設定"
          >
            <span className="library-design-strip-label">
              {authoring.admin
                ? "作成者の設定"
                : planChoice?.selected
                  ? "あなたの練習設定"
                  : "おすすめの練習方法"}
            </span>
            <dl>
              <div>
                <dt>練習方法</dt>
                <dd>
                  {methodLabel(
                    plan?.practiceMethod ?? collection.practiceMethod,
                  )}
                </dd>
              </div>
              <div>
                <dt>マスターの目標</dt>
                <dd>
                  各ドリル{" "}
                  {
                    (plan?.masteryPlan ?? collection.masteryPlan).intervalsDays
                      .length
                  }
                  回達成
                </dd>
              </div>
            </dl>
            {!authoring.admin && planChoice && (
              <button
                type="button"
                className="secondary-button"
                onClick={onEditPlan}
              >
                {planChoice.selected ? "練習設定を変更" : "練習方法を選ぶ"}
              </button>
            )}
          </section>
        )}
        {collection.isPlaylist && (
          <p className="skill-picker-count">
            元のドリルを開いて練習します。各ドリルの練習方法と進捗が使われます。
          </p>
        )}
        {drills?.length === 0 &&
          collection.canEdit &&
          !collection.isPlaylist && (
            <section className="library-design-empty">
              <div className="drill-empty-symbol">
                <PracticeToolIcon name="drills" size={40} />
              </div>
              <h2>最初のドリルを追加しましょう</h2>
              <p>
                保存した配置図に目標を添えて、個別のドリルを追加しましょう。
              </p>
              <div className="drill-empty-steps">
                <span>
                  <b>01</b> 配置を選ぶ
                </span>
                <span>
                  <b>02</b> 目標を決める
                </span>
                <span>
                  <b>03</b> 繰り返し撞く
                </span>
              </div>
              <a className="primary-button" href={createHref}>
                最初のドリルを追加 <DrillIcon name="arrow" size={18} />
              </a>
              <a className="text-link" href={authoring.href("/layouts/new")}>
                先に配置図を作成する <DrillIcon name="external" size={14} />
              </a>
            </section>
          )}
        {drills.length === 0 &&
          (!collection.canEdit || collection.isPlaylist) && (
            <section className="library-design-empty">
              <h2>
                {collection.isPlaylist
                  ? "表示できるドリルがありません"
                  : "ドリルは準備中です"}
              </h2>
              <p>
                {collection.isPlaylist
                  ? "元のドリルへのアクセスを確認してください。"
                  : "提供者がドリルを追加すると、ここから練習できます。"}
              </p>
            </section>
          )}
        {drills && drills.length > 0 && (
          <div
            className={`library-design-columns${authoring.admin ? "" : " content-grid"}`}
          >
            <div className="library-design-main content-span-2">
              <div className="library-design-section-heading">
                <h2>
                  ドリル <span>{collection.drillCount}</span>
                </h2>
                <p>
                  {authoring.admin
                    ? "配置と説明を確認し、公開する内容を整えます。"
                    : "配置を選んで練習を始めましょう。"}
                </p>
                {collection.canEdit &&
                  !collection.isPlaylist &&
                  collection.drillCount > 1 && (
                    <button
                      type="button"
                      className="secondary-button library-design-reorder-button"
                      ref={orderButton}
                      onClick={() => setReordering(true)}
                    >
                      <DrillIcon name="sort" size={18} /> 並べ替え
                    </button>
                  )}
              </div>
              <div className="library-design-toolbar">
                {!authoring.admin && (
                  <fieldset
                    className="library-design-filters"
                    aria-label="達成状況で絞り込み"
                  >
                    {(
                      [
                        ["all", "すべて", drills.length],
                        [
                          "learning",
                          "未マスター",
                          drills.length - masteredCount,
                        ],
                        ["mastered", "マスター", masteredCount],
                      ] as const
                    ).map(([value, label, count]) => (
                      <button
                        type="button"
                        key={value}
                        aria-pressed={filter === value}
                        onClick={() => setFilter(value)}
                      >
                        {label} <span>{count}</span>
                      </button>
                    ))}
                  </fieldset>
                )}
                <label className="drill-search">
                  <DrillIcon name="search" size={17} />
                  <input
                    type="search"
                    aria-label="ドリルを検索"
                    placeholder="ドリル・配置図を検索"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                  />
                </label>
              </div>
              <p className="drill-sr-only" role="status">
                {shown?.length}件のドリル
              </p>
              <LayoutPreviewProvider>
                {groups
                  .filter((group) => group.drills.length > 0)
                  .map((group) => (
                    <section
                      className="library-design-practice-group"
                      key={group.id}
                      aria-label={groupTitles[group.id]}
                    >
                      {!authoring.admin && (
                        <div className="library-design-group-heading">
                          <h2>
                            {groupTitles[group.id]}{" "}
                            <span>{group.drills.length}</span>
                          </h2>
                          {group.id === "waiting" && (
                            <p>
                              次の目安日を待っているドリルです。早めの練習もできます。
                            </p>
                          )}
                        </div>
                      )}
                      <div
                        className={`library-design-drill-grid${authoring.admin ? " content-grid content-grid-cards" : " library-design-drill-list"}`}
                      >
                        {group.drills.map((drill) => (
                          <article
                            className={`library-design-drill${authoring.admin ? "" : " library-design-drill-row"}`}
                            data-practice-state={
                              group.id === "all"
                                ? undefined
                                : group.id === "ready" &&
                                    drill.mastery.completedCount === 0
                                  ? "unstarted"
                                  : group.id
                            }
                            key={drill.id}
                          >
                            <div className="library-design-drill-summary">
                              <div className="library-design-drill-art">
                                <LayoutPreview
                                  id={drill.layout.id}
                                  title={drill.title}
                                  revision={drill.layout.revision}
                                  href={null}
                                />
                              </div>
                              <div className="library-design-drill-body">
                                <h3>
                                  <a
                                    className="library-design-drill-link"
                                    href={authoring.href(`/drills/${drill.id}`)}
                                  >
                                    {drill.title}
                                    <DrillIcon name="arrow" size={18} />
                                  </a>
                                </h3>
                                <p className="library-design-drill-goal">
                                  {drill.goal ||
                                    "成功条件を決めて、練習を始めましょう。"}
                                </p>
                                {collection.isPlaylist && (
                                  <p className="skill-picker-count">
                                    {drill.collection.title} ·
                                    練習方法はドリルで確認
                                  </p>
                                )}
                                {!authoring.admin && (
                                  <MasteryProgress drill={drill} compact />
                                )}
                                {!authoring.admin && (
                                  <div className="library-design-drill-footer">
                                    <span>
                                      {drill.practices[0]
                                        ? `前回 ${dateLabel(drill.practices[0].createdAt)}`
                                        : "練習記録なし"}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                            {!authoring.admin && (
                              <DrillPerformance drill={drill} />
                            )}
                          </article>
                        ))}
                      </div>
                    </section>
                  ))}
              </LayoutPreviewProvider>
              {shown?.length === 0 && (
                <div className="library-design-empty library-design-filter-empty">
                  <h2>一致するドリルがありません</h2>
                  <p>検索条件や達成状況の絞り込みを変更してください。</p>
                  <button
                    type="button"
                    className="secondary-button"
                    onClick={() => {
                      setQuery("");
                      setFilter("all");
                    }}
                  >
                    すべてのドリルを表示
                  </button>
                </div>
              )}
              {collection.drillCount > drills.length && (
                <p className="drill-footnote">
                  このドリル集の{collection.drillCount}件中、先頭{drills.length}
                  件を読み込んでいます。
                </p>
              )}
            </div>
            {!authoring.admin && (
              <div
                className="library-design-reserved-column"
                aria-hidden="true"
              />
            )}
          </div>
        )}
      </div>
    </DrillShell>
  );
}
